import { articleContent, type ContentBlock } from '@/lib/articleContent';

export type PaidRequirement = 'yes' | 'no' | 'depends';

export type TicketDepartment = {
  id: string;
  name: string;
  description: string;
  examples: string[];
  paidRequired: PaidRequirement;
  paidNote?: string;
};

export type TicketTimelineStep = {
  id: string;
  title: string;
  description: string;
};

export type TicketPracticeCard = {
  id: string;
  kind: 'good' | 'avoid';
  title: string;
  description: string;
};

export type TicketFaqItem = {
  id: string;
  question: string;
  answer: string;
};

export type TicketCallout = {
  type: 'info' | 'tip' | 'warning' | 'success';
  title: string;
  content: string;
};

export type TicketRelatedLink = {
  label: string;
  href: string;
};

export type TicketExample = {
  id: string;
  occasion: string;
  department: string;
  subject: string;
  message: string;
  whyItWorks: string;
};

export type XatTicketsGuideData = {
  whenToOpen: string[];
  whenNotToOpen: string[];
  paidUserHighlight: string;
  timeline: TicketTimelineStep[];
  departments: TicketDepartment[];
  practices: TicketPracticeCard[];
  exampleTickets: TicketExample[];
  responseTime: string;
  trackTicketSteps: { title: string; description: string }[];
  trackTicketNote: string;
  faq: TicketFaqItem[];
  callouts: TicketCallout[];
  relatedLinks: TicketRelatedLink[];
};

const DEPARTMENT_META: Record<
  string,
  { examples: string[]; paidRequired: PaidRequirement; paidNote?: string }
> = {
  'A General Question or Concern': {
    examples: ['Dúvida geral sobre o xat', 'Assunto que não se encaixa em outro tópico'],
    paidRequired: 'depends',
    paidNote: 'Aberto a todos; use apenas quando nenhum outro tópico se aplicar.',
  },
  'Account Block': {
    examples: ['Erro 25 ou 55 ao tradear/transferir', 'Conta held há mais de 7 dias', 'Conta ou xatspace excluídos'],
    paidRequired: 'no',
  },
  'Account Hold': {
    examples: ['Conta em hold há menos de 7 dias', 'Transferências temporariamente bloqueadas'],
    paidRequired: 'no',
  },
  'Chat Block': {
    examples: ['Chat bloqueado de promoção', 'Conteúdo removido do grupo'],
    paidRequired: 'depends',
    paidNote: 'Gestores de chat podem precisar comprovar propriedade do grupo.',
  },
  'Domain Unblock': {
    examples: ['Domínio bloqueado no LinkValidator', 'Link bloqueado abaixo do chat'],
    paidRequired: 'depends',
  },
  'Email Change / Hint': {
    examples: ['Trocar e-mail de conta que você ainda acessa', 'Atualizar para Gmail por segurança'],
    paidRequired: 'no',
    paidNote: 'Apenas Gmail é aceite por motivos de segurança.',
  },
  'Location Update': {
    examples: ['Atualizar IP ou país', 'Ativar Account Protection após mudança de localização'],
    paidRequired: 'no',
  },
  'Locked Out': {
    examples: ['Erro E29 — bloqueado por mudança de localização', 'Não consegue fazer login na conta'],
    paidRequired: 'no',
  },
  'Lost Access': {
    examples: ['Esqueceu e-mail e senha', 'Senha perdida sem acesso ao e-mail'],
    paidRequired: 'no',
  },
  'Official Chats': {
    examples: ['Gestores de chats oficiais', 'Assuntos administrativos de chat oficial'],
    paidRequired: 'depends',
    paidNote: 'Destinado a gestores autorizados de chats oficiais.',
  },
  'Payment Problems': {
    examples: ['Xats/days não creditados após compra', 'Problemas com reserve ou pagamento'],
    paidRequired: 'yes',
    paidNote: 'Exige compra prévia na loja oficial do xat.',
  },
  'Report Phishing Site': {
    examples: ['Sites de phishing', 'Servidores privados ou sites contra os Termos'],
    paidRequired: 'no',
  },
  'Report Scam': {
    examples: ['Foi scammado ou phished', 'Faltam xats — inclua provas (prints)'],
    paidRequired: 'no',
  },
  'Short Names and Groups': {
    examples: ['Transferir short name', 'Recolocar nome ou grupo à venda', 'Reativar grupo comprado'],
    paidRequired: 'yes',
  },
  'Supporter and Celebrity Requests': {
    examples: ['Pedido de status Supporter', 'Pedido de Celebrity — inclua links de redes sociais'],
    paidRequired: 'yes',
  },
  'xat chat / site issues': {
    examples: ['Bugs no chat ou no site', 'Erros nos serviços — descreva passos para reproduzir'],
    paidRequired: 'no',
  },
};

const WHEN_TO_OPEN = [
  'Problemas na conta (bloqueio, hold, locked out)',
  'Compras e pagamentos não creditados',
  'Trade Lock ou erros 25/55',
  'Conta bloqueada ou excluída',
  'Short Names e grupos',
  'Powers e recursos da conta',
  'Cobranças e reserve',
  'Reportar scam ou phishing com provas',
];

const WHEN_NOT_TO_OPEN = [
  'Dúvidas simples sobre o funcionamento do xat',
  'Perguntas sobre powers (consulte a Powerpédia)',
  'Como usar o chat, smilies ou comandos básicos',
  'Problemas que já têm solução em artigos do xat Ajuda',
  'Assuntos gerais que podem ser resolvidos no chat Help',
];

const PAID_USER_HIGHLIGHT =
  'Alguns departamentos do sistema de tickets exigem que o usuário tenha feito pelo menos uma compra diretamente na loja oficial do xat (xats ou days). Usuários que nunca compraram podem abrir tickets apenas em determinadas categorias — como Lost Access, Locked Out ou Report Scam — ou utilizar os canais alternativos de suporte, como o fórum xat (forum.xat.com) na secção General Support e o chat Help.';

const TIMELINE: TicketTimelineStep[] = [
  {
    id: 'access',
    title: 'Entrar no sistema',
    description:
      'Acesse xat.com/ticket no navegador. Você precisa de uma conta registrada para criar ou consultar tickets.',
  },
  {
    id: 'login',
    title: 'Fazer login',
    description:
      'Confirme usuário, senha e e-mail da conta. Se não conseguir logar, abra o ticket por uma conta alternativa (veja o guia de registro).',
  },
  {
    id: 'department',
    title: 'Escolher o departamento correto',
    description:
      'No campo «Help Topic», selecione o assunto mais próximo do seu problema. Tickets no tópico errado demoram mais ou podem não ser respondidos.',
  },
  {
    id: 'subject',
    title: 'Escrever um bom título',
    description:
      'Use um resumo curto e claro em inglês (ex.: «Locked out of ID 12345678» ou «Trade Lock after IP change»).',
  },
  {
    id: 'details',
    title: 'Enviar detalhes completos',
    description:
      'Explique o problema com ID, e-mail, IP antigo, xats/days na conta, provedor, localização anterior e prints quando necessário.',
  },
  {
    id: 'wait',
    title: 'Aguardar resposta',
    description:
      'Clique em «Open Ticket». Você receberá e-mail quando houver resposta. Só é permitido um ticket aberto por conta.',
  },
];

const PRACTICES: TicketPracticeCard[] = [
  {
    id: 'explain',
    kind: 'good',
    title: 'Explique exatamente o problema',
    description: 'Descreva o que aconteceu, quando começou e o que você já tentou fazer.',
  },
  {
    id: 'ids',
    kind: 'good',
    title: 'Informe IDs',
    description: 'Inclua seu xat ID, e-mail da conta e, se relevante, ID do outro usuário ou do chat.',
  },
  {
    id: 'dates',
    kind: 'good',
    title: 'Informe datas',
    description: 'Mencione quando o problema começou e há quanto tempo a conta está bloqueada ou em hold.',
  },
  {
    id: 'screenshots',
    kind: 'good',
    title: 'Anexe prints quando necessário',
    description: 'Especialmente em Report Scam, Payment Problems ou bugs — imagens aceleram a análise.',
  },
  {
    id: 'vague',
    kind: 'avoid',
    title: 'Escrever apenas «não funciona»',
    description: 'Mensagens vagas exigem idas e vindas e atrasam a resolução.',
  },
  {
    id: 'multiple',
    kind: 'avoid',
    title: 'Criar vários tickets para o mesmo problema',
    description: 'Só é permitido um ticket aberto por conta. Aguarde a resposta ou peça reabertura a um voluntário.',
  },
  {
    id: 'portuguese',
    kind: 'avoid',
    title: 'Enviar mensagens em português',
    description:
      'Tickets devem ser escritos em inglês — é o idioma do sistema oficial. Use frases completas; um tradutor pode ajudar.',
  },
];

const EXAMPLE_TICKETS: TicketExample[] = [
  {
    id: 'trade-lock',
    occasion: 'Trade Lock ou erro 25/55',
    department: 'Account Block',
    subject: 'Trade Lock after IP change',
    message: `Hello,

My account has been under Trade Lock since yesterday after changing my IP address.

My xat ID is 12345678 and my registered email is [email protected].

Could you please verify if there are any additional steps required?

Thank you.`,
    whyItWorks:
      'Identifica o problema, informa quando começou, inclui ID e e-mail, e faz um pedido claro — tudo em inglês.',
  },
  {
    id: 'locked-out',
    occasion: 'Bloqueado por mudança de localização (E29)',
    department: 'Locked Out',
    subject: 'Locked out of my account (E29)',
    message: `Hello,

I am locked out of my account after travelling to another country. I receive error E29 when trying to log in.

Username: MyName
ID: 12345678
Registered email: [email protected]
Previous location: Brazil
Current location: Portugal
ISP: Example ISP

I can provide additional proof if needed.

Thank you.`,
    whyItWorks:
      'O tópico Locked Out pede localização antiga e nova, ISP e dados da conta — este exemplo entrega tudo de uma vez.',
  },
  {
    id: 'lost-access',
    occasion: 'Esqueceu e-mail ou senha',
    department: 'Lost Access',
    subject: 'Lost access to account — forgot password',
    message: `Hello,

I cannot log in because I forgot my password and no longer have access to the email on file.

My xat ID is 12345678.
My username is MyName.
I last logged in approximately two weeks ago from Brazil.

I remember having around 5000 xats and 30 days on the account. My old ISP was Example ISP.

Please let me know what information you need to verify ownership.

Thank you.`,
    whyItWorks:
      'Lost Access exige provas de titularidade — ID, username, recursos na conta, ISP antigo e datas ajudam o voluntário a verificar você.',
  },
  {
    id: 'payment',
    occasion: 'Xats ou days não creditados',
    department: 'Payment Problems',
    subject: 'Xats not credited after purchase',
    message: `Hello,

I purchased 33000 xats on 05/07/2026 via the official xat store but they have not been added to my account.

Transaction ID: XXXXX-XXXXX
Payment method: PayPal
Account ID: 12345678
Registered email: [email protected]

I have attached the payment receipt screenshot.

Thank you.`,
    whyItWorks:
      'Payment Problems exige usuário pago e detalhes da transação — data, método, ID da compra e print do comprovativo aceleram o crédito.',
  },
  {
    id: 'report-scam',
    occasion: 'Foi scammado ou perdeu xats',
    department: 'Report Scam',
    subject: 'Report scam — lost 50000 xats',
    message: `Hello,

I was scammed today by user ID 87654321 (username ScammerName).

I transferred 50000 xats in a trade. The user edited the trade using an external program before I confirmed.

I have screenshots of the trade window before and after, and the chat log.

My ID: 12345678

Please review this case.

Thank you.`,
    whyItWorks:
      'Report Scam precisa de provas — IDs de ambos os usuários, valor, descrição do que aconteceu e prints são essenciais.',
  },
  {
    id: 'short-name',
    occasion: 'Short name ou grupo',
    department: 'Short Names and Groups',
    subject: 'Request to relist short name',
    message: `Hello,

I would like to relist my short name "MyChat" for sale.

My xat ID is 12345678.
I am a paid user and purchased this short name previously.

The name was delisted on 01/06/2026. Please confirm the steps to put it back on sale.

Thank you.`,
    whyItWorks:
      'Short Names and Groups exige usuário pago — menciona isso, o nome exato, ID e data do delist.',
  },
  {
    id: 'account-hold',
    occasion: 'Conta em hold recente',
    department: 'Account Hold',
    subject: 'Account hold — less than 7 days',
    message: `Hello,

My account was placed on hold three days ago and I cannot transfer xats.

ID: 12345678
Email: [email protected]

I have not made any suspicious trades. Could you explain why the hold was applied and when it will be lifted?

Thank you.`,
    whyItWorks:
      'Account Hold é para holds recentes (menos de 7 dias) — informa há quanto tempo, ID e pede esclarecimento objetivo.',
  },
  {
    id: 'location-update',
    occasion: 'Atualizar localização / Account Protection',
    department: 'Location Update',
    subject: 'Location update for Account Protection',
    message: `Hello,

I moved permanently from Brazil to Portugal and need my home location updated to use Account Protection.

ID: 12345678
Username: MyName
Old IP range: (if known)
New country: Portugal

I can provide additional verification if required.

Thank you.`,
    whyItWorks:
      'Location Update pede mudança de IP/país — deixa claro o motivo (Account Protection) e os dados antigos e novos.',
  },
];

const RESPONSE_TIME =
  'Cada departamento possui um tempo de resposta diferente — alguns são atendidos em poucas horas, outros podem levar dias conforme a fila e a complexidade do caso. Consulte o artigo «Ticket times» na wiki xat para ver o estado atual de cada secção. Casos que exigem verificação manual (hold, scam, pagamentos) costumam demorar mais.';

const FAQ: TicketFaqItem[] = [
  {
    id: 'multiple-tickets',
    question: 'Posso abrir mais de um ticket?',
    answer:
      'Não. Só é permitido um ticket aberto por conta. Aguarde a resposta do voluntário (a resposta encerra o ticket) ou peça a um voluntário no chat Help para orientação sobre reabertura.',
  },
  {
    id: 'portuguese',
    question: 'Posso responder em português?',
    answer:
      'A interface do ticket pode aparecer no idioma do navegador, mas o suporte funciona em inglês. Escreva tickets e respostas em inglês — você pode ser orientado a usar um tradutor.',
  },
  {
    id: 'edit',
    question: 'Posso editar um ticket enviado?',
    answer:
      'Não é possível editar após o envio, mas você pode responder ao próprio ticket com informações adicionais em «Check Status» em xat.com/ticket.',
  },
  {
    id: 'notification',
    question: 'Como saber se responderam?',
    answer:
      'Você receberá um e-mail do xat quando houver resposta. Também pode consultar em xat.com/ticket → «Check Status» com sua senha.',
  },
  {
    id: 'wrong-department',
    question: 'O que acontece se eu escolher o departamento errado?',
    answer:
      'O ticket pode demorar mais, ser transferido internamente ou não receber resposta. Escolha sempre o tópico mais específico para o seu caso.',
  },
  {
    id: 'paid-required',
    question: 'Preciso ser usuário pago?',
    answer:
      'Depende do departamento. Payment Problems, Short Names and Groups e Supporter and Celebrity exigem compra prévia na loja oficial. Lost Access, Locked Out, Account Block e Report Scam estão disponíveis sem compra.',
  },
];

function slugifyDepartment(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function parseDepartmentsFromBlocks(blocks: ContentBlock[]): TicketDepartment[] {
  const departments: TicketDepartment[] = [];

  for (const block of blocks) {
    if (block.type !== 'table' || block.headers[0] !== 'Tópico') continue;

    for (const row of block.rows) {
      const name = row[0]?.trim() ?? '';
      const description = row[1]?.trim() ?? '';
      if (!name) continue;

      const meta = DEPARTMENT_META[name] ?? {
        examples: [],
        paidRequired: 'depends' as PaidRequirement,
      };

      departments.push({
        id: slugifyDepartment(name),
        name,
        description,
        examples: meta.examples,
        paidRequired: meta.paidRequired,
        paidNote: meta.paidNote,
      });
    }
  }

  return departments;
}

function parseTrackTicketFromBlocks(blocks: ContentBlock[]): {
  steps: { title: string; description: string }[];
  note: string;
} {
  const steps: { title: string; description: string }[] = [];
  let note = '';
  let inTrackSection = false;

  for (const block of blocks) {
    if (block.type === 'heading' && block.text === 'Ver e responder a um ticket aberto') {
      inTrackSection = true;
      continue;
    }
    if (inTrackSection && block.type === 'heading') break;

    if (inTrackSection && block.type === 'steps') {
      for (const item of block.items) {
        steps.push({ title: item.title, description: item.description });
      }
    }
    if (inTrackSection && block.type === 'paragraph') {
      note = block.text;
    }
  }

  return { steps, note };
}

function parseCalloutsFromBlocks(blocks: ContentBlock[]): TicketCallout[] {
  const callouts: TicketCallout[] = [];
  const seen = new Set<string>();

  for (const block of blocks) {
    if (block.type === 'info') {
      const key = `info-${block.title}`;
      if (!seen.has(key)) {
        seen.add(key);
        callouts.push({ type: 'info', title: block.title, content: block.content });
      }
    } else if (block.type === 'warning') {
      const key = `warning-${block.title}`;
      if (!seen.has(key)) {
        seen.add(key);
        callouts.push({ type: 'warning', title: block.title, content: block.content });
      }
    } else if (block.type === 'tip') {
      const key = `tip-${block.title}`;
      if (!seen.has(key)) {
        seen.add(key);
        callouts.push({ type: 'tip', title: block.title, content: block.content });
      }
    }
  }

  return callouts;
}

function buildRelatedLinks(): TicketRelatedLink[] {
  return [
    { label: 'Recuperar conta (senha e acesso)', href: '/account/redefinir-sua-senha-com-seguranca' },
    { label: 'Trade Lock e erros de sistema', href: '/troubleshooting/erros-do-xat#category-sistema' },
    { label: 'Lista completa de erros', href: '/troubleshooting/erros-do-xat' },
    { label: 'Erros de conta e bloqueios', href: '/troubleshooting/erros-do-xat#category-conta' },
    { label: 'Métodos de pagamento aceitos', href: '/store/metodos-de-pagamento-aceitos' },
  ];
}

const EXTRA_CALLOUTS: TicketCallout[] = [
  {
    type: 'tip',
    title: 'Dica',
    content: 'Sempre envie o máximo possível de informações — ID, e-mail, datas, prints e o que você já tentou.',
  },
  {
    type: 'warning',
    title: 'Atenção',
    content: 'Nunca informe sua senha no ticket. O suporte xat nunca pedirá sua senha por mensagem.',
  },
  {
    type: 'warning',
    title: 'Evite',
    content: 'Não abra vários tickets para o mesmo problema. Um ticket aberto por conta é a regra do sistema.',
  },
];

let cachedData: XatTicketsGuideData | null = null;

export function getXatTicketsGuideData(): XatTicketsGuideData {
  if (cachedData) return cachedData;

  const article = articleContent.troubleshooting[5];
  if (!article) {
    cachedData = {
      whenToOpen: WHEN_TO_OPEN,
      whenNotToOpen: WHEN_NOT_TO_OPEN,
      paidUserHighlight: PAID_USER_HIGHLIGHT,
      timeline: TIMELINE,
      departments: [],
      practices: PRACTICES,
      exampleTickets: EXAMPLE_TICKETS,
      responseTime: RESPONSE_TIME,
      trackTicketSteps: [],
      trackTicketNote: '',
      faq: FAQ,
      callouts: EXTRA_CALLOUTS,
      relatedLinks: buildRelatedLinks(),
    };
    return cachedData;
  }

  const { steps: trackTicketSteps, note: trackTicketNote } = parseTrackTicketFromBlocks(article.blocks);
  const parsedCallouts = parseCalloutsFromBlocks(article.blocks);

  const calloutTitles = new Set(EXTRA_CALLOUTS.map((c) => c.title));
  const mergedCallouts = [
    ...EXTRA_CALLOUTS,
    ...parsedCallouts.filter((c) => !calloutTitles.has(c.title)),
  ];

  cachedData = {
    whenToOpen: WHEN_TO_OPEN,
    whenNotToOpen: WHEN_NOT_TO_OPEN,
    paidUserHighlight: PAID_USER_HIGHLIGHT,
    timeline: TIMELINE,
    departments: parseDepartmentsFromBlocks(article.blocks),
    practices: PRACTICES,
    exampleTickets: EXAMPLE_TICKETS,
    responseTime: RESPONSE_TIME,
    trackTicketSteps,
    trackTicketNote,
    faq: FAQ,
    callouts: mergedCallouts,
    relatedLinks: buildRelatedLinks(),
  };

  return cachedData;
}

export function isXatTicketsGuideArticle(categoryId: string, articleSlug: string): boolean {
  return categoryId === 'troubleshooting' && articleSlug === 'sistema-de-tickets-do-xat';
}

export const PAID_REQUIREMENT_LABELS: Record<PaidRequirement, string> = {
  yes: 'Sim',
  no: 'Não',
  depends: 'Depende',
};
